// src/components/BeforeAfterSlider.js
import React, { useState, useEffect, useRef } from 'react';
import './BeforeAfterSlider.css';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsAltH,
  faHome,
  faCheck,
  faMedal,
  faToolbox
} from '@fortawesome/free-solid-svg-icons';

function BeforeAfterSlider() {
  // Start fully revealing the "after" image so only one image is visible
  const [sliderPosition, setSliderPosition] = useState(100);
  const [isDragging, setIsDragging] = useState(false);
  const [animated, setAnimated] = useState(false);
  const sliderRef = useRef(null);

  // Animation on scroll
  useEffect(() => {
    const handleScroll = () => {
      const element = document.querySelector('.before-after-section');
      if (!element) return;

      const position = element.getBoundingClientRect();
      if (position.top < window.innerHeight * 0.75 && !animated) {
        setAnimated(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [animated]);

  // Handle mouse down event on slider
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // Handle touch start on mobile
  const handleTouchStart = () => {
    setIsDragging(true);
  };

  // Handle mouse move event for sliding
  const handleMove = (clientX) => {
    if (!isDragging || !sliderRef.current) return;

    const sliderRect = sliderRef.current.getBoundingClientRect();
    const newPosition = ((clientX - sliderRect.left) / sliderRect.width) * 100;

    // Limit position between 0 and 100
    const limitedPosition = Math.max(0, Math.min(100, newPosition));
    setSliderPosition(limitedPosition);
  };

  // Handle mouse up event to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add event listeners for mouse and touch events
  useEffect(() => {
    const handleMouseMove = (e) => handleMove(e.clientX);
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX);
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <section className="before-after-section">
      <div className="container">
        <div className={`section-header ${animated ? 'animated' : ''}`}>
          <h2 className="section-title">Transforming Roofs Across Glossop</h2>
          <p className="section-description">
            See the remarkable difference our expert craftsmanship makes
          </p>
        </div>

        <div className={`slider-container ${animated ? 'animated' : ''}`} ref={sliderRef}>
          <div className="image-container">
            {/* Before image */}
            <div className="before-image"></div>

            {/* After image - clipped based on slider position */}
            <div
              className="after-image"
              style={{ width: `${sliderPosition}%` }}
            ></div>

            {/* Slider control */}
            <div
              className="slider-control"
              style={{ left: `calc(${sliderPosition}% - 20px)` }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <div className="slider-line"></div>
              <div className="slider-button">
                <FontAwesomeIcon icon={faArrowsAltH} />
              </div>
              <div className="slider-line"></div>
            </div>
          </div>

          <div className="project-info">
            <h3>Roof Transformation Project</h3>
            <div className="project-location">Glossop</div>
            <p className="project-description">
              Complete roof replacement transforming this property with quality craftsmanship.
            </p>
          </div>
        </div>

        {/* <div className={`transformation-stats ${animated ? 'animated' : ''}`}> // REMOVED THIS SECTION
          <div className="stat-item">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faHome} />
            </div>
            <div className="stat-number">200+</div>
            <div className="stat-label">Projects Completed</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faMedal} />
            </div>
            <div className="stat-number">98%</div>
            <div className="stat-label">Customer Satisfaction</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faToolbox} />
            </div>
            <div className="stat-number">15+</div>
            <div className="stat-label">Years Experience</div>
          </div>
        </div> */}

        <div className={`transformation-cta ${animated ? 'animated' : ''}`}>
          <a href="/gallery" className="view-more-btn">
            View More Projects <FontAwesomeIcon icon={faCheck} className="btn-icon" />
          </a>
        </div>
      </div>
    </section>
  );
}

export default BeforeAfterSlider;